import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, Grid, LinearProgress, List, ListItem, ListItemText, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { runDualAUS, generateAUSInsights, saveAUSSelection, AUS_SYSTEMS, REP_WARRANTY_STATUS } from '../../../services/ausService';
import { useToast } from '../../../components/shared/Toast';
import StatusBadge, { AUS_RECOMMENDATION_MAP, REP_WARRANTY_MAP } from '../../../components/shared/StatusBadge';
import SectionHeader from '../../../components/shared/SectionHeader';
import CompareIcon from '@mui/icons-material/Compare';
import TimelineIcon from '@mui/icons-material/Timeline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VerifiedIcon from '@mui/icons-material/Verified';

const DualAUSComponent = ({ loan, onAUSComplete }) => {
  const [ausResults, setAusResults] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAUS, setSelectedAUS] = useState(null);
  const [ausHistory, setAusHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const { showToast, ToastContainer } = useToast();

  const handleRunAUS = async () => {
    setLoading(true);
    try {
      const results = await runDualAUS(loan);
      setAusResults(results);
      const insights = generateAUSInsights(results.du, results.lpa, loan);
      setAiInsights(insights);
      setAusHistory(prev => [...prev, { timestamp: results.timestamp, du: results.du, lpa: results.lpa, runNumber: prev.length + 1 }]);
    } catch (error) {
      console.error('AUS execution error:', error);
      showToast('Failed to run AUS. Please try again.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAUS = async (system) => {
    if (!ausResults) return;
    const selection = await saveAUSSelection(loan.id, system, ausResults);
    setSelectedAUS(system);
    if (onAUSComplete) onAUSComplete(system, ausResults);
    showToast(`${system} selected as governing AUS findings. This selection has been logged.`);
  };

  const getRepWarrantyIcon = (status) => {
    if (status === REP_WARRANTY_STATUS.GRANTED) return <CheckCircleIcon color="success" />;
    if (status === REP_WARRANTY_STATUS.PARTIAL) return <WarningIcon color="warning" />;
    return <CancelIcon color="error" />;
  };

  const getRepWarrantyBadge = (status) => <StatusBadge status={status} map={REP_WARRANTY_MAP} />;

  const getRecommendationBadge = (code) => (
    <StatusBadge status={code} map={AUS_RECOMMENDATION_MAP} style={{ fontSize: '1.1rem', padding: '0.5rem 1rem' }}>
      {code === 'approve' || code === 'accept' ? '✓ Approved' : code === 'refer' || code === 'caution' ? '⚠ Refer' : '✗ Ineligible'}
    </StatusBadge>
  );

  if (!ausResults && !loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <SmartToyIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Dual AUS - Underwriting Intelligence</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Run Desktop Underwriter (DU) and Loan Product Advisor (LPA) simultaneously to compare findings and Rep & Warranty relief.</Typography>
          <Button variant="contained" size="large" startIcon={<PlayArrowIcon />} onClick={handleRunAUS}>Run AUS (DU + LPA)</Button>
          {ausHistory.length > 0 && (
            <Box sx={{ mt: 3 }}><Button variant="text" startIcon={<HistoryIcon />} onClick={() => setShowHistory(!showHistory)}>View AUS History ({ausHistory.length} runs)</Button></Box>
          )}
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Running Dual AUS...</Typography>
          <Typography variant="body2" color="text.secondary">Executing Desktop Underwriter and Loan Product Advisor in parallel</Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const renderRepWarrantyCell = (data) => (
    <TableCell align="center">
      <Box sx={{ mb: 0.5 }}>{getRepWarrantyIcon(data.status)}</Box>
      {getRepWarrantyBadge(data.status)}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{data.explanation}</Typography>
    </TableCell>
  );

  const renderAUSCard = (system, results, label, color) => (
    <Grid size={{ xs: 12, lg: 6 }}>
      <Card variant="outlined" sx={{ borderColor: selectedAUS === system ? 'primary.main' : undefined, borderWidth: selectedAUS === system ? 2 : 1 }}>
        <CardHeader title={<SectionHeader title={label} sx={{ mb: 0 }} />} action={selectedAUS === system ? <StatusBadge variant="primary">Selected AUS</StatusBadge> : null} sx={{ bgcolor: `${color}.50` }} />
        <CardContent>
          <Box sx={{ textAlign: 'center', mb: 2 }}>{getRecommendationBadge(results.recommendationCode)}<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Casefile: {results.casefileId}</Typography></Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Key Messages</Typography>
          <List dense sx={{ mb: 1 }}>{results.messages.map((msg, idx) => <ListItem key={idx} sx={{ pl: 0 }}><ListItemText primary={msg} primaryTypographyProps={{ variant: 'body2' }} /></ListItem>)}</List>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Findings Summary</Typography>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell>Credit Score</TableCell><TableCell align="right"><strong>{results.findings.creditScore}</strong></TableCell></TableRow>
              <TableRow><TableCell>LTV</TableCell><TableCell align="right"><strong>{results.findings.ltv}%</strong></TableCell></TableRow>
              <TableRow><TableCell>DTI</TableCell><TableCell align="right"><strong>{results.findings.dti}%</strong></TableCell></TableRow>
            </TableBody>
          </Table>
          {!selectedAUS && <Button variant="contained" color={color === 'info' ? 'primary' : 'success'} fullWidth startIcon={<CheckIcon />} sx={{ mt: 2 }} onClick={() => handleSelectAUS(system)}>Proceed with {system === AUS_SYSTEMS.DU ? 'DU' : 'LPA'}</Button>}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box>
      {ToastContainer}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title={<SectionHeader title="Dual AUS Results" icon={<CompareIcon fontSize="small" />} sx={{ mb: 0 }} />} action={<Stack direction="row" spacing={1}><Button size="small" variant="contained" startIcon={<RefreshIcon />} onClick={handleRunAUS}>Re-run AUS</Button><Button size="small" variant="text" startIcon={<HistoryIcon />} onClick={() => setShowHistory(!showHistory)}>History ({ausHistory.length})</Button></Stack>} />
      </Card>

      {showHistory && ausHistory.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="AUS History Timeline" icon={<TimelineIcon fontSize="small" />} sx={{ mb: 0 }} />} />
          <CardContent>
            <List dense>
              {ausHistory.map((run, idx) => (
                <ListItem key={idx}>
                  <Chip label={`Run #${run.runNumber}`} size="small" color="primary" variant="outlined" sx={{ mr: 1 }} />
                  <ListItemText primary={new Date(run.timestamp).toLocaleString()} secondary={`DU: ${run.du.recommendation} | LPA: ${run.lpa.recommendation}`} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {aiInsights && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardHeader title={<SectionHeader title="AI AUS Insights" icon={<AutoAwesomeIcon fontSize="small" />} sx={{ mb: 0 }} />} sx={{ bgcolor: 'secondary.50' }} />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}><strong>Summary:</strong> {aiInsights.summary}</Alert>
            {aiInsights.differences.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Key Differences</Typography>
                {aiInsights.differences.map((diff, idx) => <Alert key={idx} severity="warning" sx={{ mb: 1 }}><strong>{diff.category}:</strong> DU = {diff.du}, LPA = {diff.lpa}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{diff.explanation}</Typography></Alert>)}
              </Box>
            )}
            {aiInsights.repWarrantyAnalysis.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Rep & Warranty Analysis</Typography>
                {aiInsights.repWarrantyAnalysis.map((a, idx) => <Alert key={idx} severity="info" sx={{ mb: 1 }}><strong>{a.category}:</strong> DU {a.du} vs LPA {a.lpa}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{a.explanation}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{a.reason}</Typography></Alert>)}
              </Box>
            )}
            {aiInsights.recommendations.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Recommendations</Typography>
                {aiInsights.recommendations.map((rec, idx) => <Alert key={idx} severity="success" sx={{ mb: 1 }}><strong>{rec.title}</strong><Typography variant="body2" color="text.secondary">{rec.description}</Typography><Chip label={`Impact: ${rec.impact}`} size="small" color="success" variant="outlined" sx={{ mt: 0.5 }} /></Alert>)}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {renderAUSCard(AUS_SYSTEMS.DU, ausResults.du, 'Desktop Underwriter (DU)', 'info')}
        {renderAUSCard(AUS_SYSTEMS.LPA, ausResults.lpa, 'Loan Product Advisor (LPA)', 'success')}
      </Grid>

      <Card variant="outlined">
        <CardHeader title={<SectionHeader title="Rep & Warranty Relief Comparison" icon={<VerifiedIcon fontSize="small" />} sx={{ mb: 0 }} />} />
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="center" sx={{ width: 200 }}>DU</TableCell>
                <TableCell align="center" sx={{ width: 200 }}>LPA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {['income', 'assets', 'employment', 'appraisal'].map(cat => (
                <TableRow key={cat}>
                  <TableCell><Typography variant="body2" fontWeight={700} sx={{ textTransform: 'capitalize' }}>{cat}</Typography><Typography variant="caption" color="text.secondary">{cat} verification relief</Typography></TableCell>
                  {renderRepWarrantyCell(ausResults.du.repWarranty[cat])}
                  {renderRepWarrantyCell(ausResults.lpa.repWarranty[cat])}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Stack direction="row" spacing={3} sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={0.5} alignItems="center"><CheckCircleIcon color="success" fontSize="small" /><Typography variant="caption">Granted = Full relief</Typography></Stack>
          <Stack direction="row" spacing={0.5} alignItems="center"><WarningIcon color="warning" fontSize="small" /><Typography variant="caption">Partial = Conditional relief</Typography></Stack>
          <Stack direction="row" spacing={0.5} alignItems="center"><CancelIcon color="error" fontSize="small" /><Typography variant="caption">Denied = No relief</Typography></Stack>
        </Stack>
      </Card>
    </Box>
  );
};

export default DualAUSComponent;
